import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { registerSW } from 'virtual:pwa-register';
// import './styles/critical.css';
import './index.css';
import App from './App.tsx';

// Define the type for the window object with our custom properties
declare global {
  interface Window {
    checkForUpdate: () => boolean;
    applyUpdate: () => void;
  }
}

// Create a global update manager
let updateAvailable = false;
let updateInProgress = false;

// Register PWA with mobile-specific error handling
let updateSW: any = null;

try {
  // Check if we're on a mobile device
  const userAgent = navigator.userAgent || '';
  const isMobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase());
  
  console.log('🔍 PWA Registration - Device Info:', { 
    userAgent, 
    isMobile,
    serviceWorker: 'serviceWorker' in navigator,
    pwaSupport: 'virtual:pwa-register' in window
  });

  if ('serviceWorker' in navigator) {
    // Register Railway-integrated service worker directly from server
    navigator.serviceWorker.register('/arcade/games/riddl/sw-custom.js', { scope: '/arcade/games/riddl/' })
      .then((registration) => {
        console.log('✅ Railway Service Worker registered successfully:', registration);
        console.log('📱 Service Worker scope:', registration.scope);
        console.log('📱 Service Worker script URL:', registration.active?.scriptURL);
        console.log('🔗 Railway service worker handles both caching AND push notifications');
        
        // Set up update checking manually since we're using Railway service worker
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                updateAvailable = true;
                console.log('🔄 Update available - Service Worker detected new version');
                console.log('🔄 Update system ready - checkForUpdate() will return true');
              }
            });
          }
        });
        
        // Create a mock updateSW function for compatibility
        updateSW = (reloadPage = true) => {
          if (reloadPage) {
            window.location.reload();
          }
          return Promise.resolve();
        };
      })
      .catch((error) => {
        console.error('❌ Custom Service Worker registration failed:', error);
        
        // Fallback to PWA service worker
        updateSW = registerSW({
          immediate: true,
          onNeedRefresh() {
            if (!updateInProgress) {
              updateAvailable = true;
              console.log('🔄 Update available - Service Worker detected new version');
              console.log('🔄 Update system ready - checkForUpdate() will return true');
            }
          },
          onOfflineReady() {
            console.log('📱 App ready to work offline');
          },
          onRegisterError(error) {
            console.error('❌ Service Worker registration failed:', error);
          }
        });
      });
  } else {
    console.log('⚠️ Service Worker not supported on this device/browser');
  }
} catch (error) {
  console.error('❌ PWA registration error:', error);
  // Continue without PWA features
}

// Expose update functions globally with fallbacks
window.checkForUpdate = () => {
  console.log('🔍 Checking for updates:', { updateAvailable, updateInProgress });
  if (updateAvailable && !updateInProgress) {
    updateAvailable = false;
    updateInProgress = true;
    console.log('✅ Update check returned true - showing update toast');
    return true;
  }
  console.log('❌ Update check returned false - no updates available');
  return false;
};

window.applyUpdate = () => {
  console.log('🚀 Applying update via service worker...');
  updateInProgress = true;
  updateAvailable = false;
  
  if (updateSW && typeof updateSW === 'function') {
    try {
      updateSW(true);
    } catch (error) {
      console.error('❌ Update application failed:', error);
      // Fallback: reload the page
      window.location.reload();
    }
  } else {
    console.log('⚠️ Service Worker not available, falling back to page reload');
    window.location.reload();
  }
};

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>
);
